import { useEffect, useState } from 'react';

import { fetchAgentManagerFileBlob } from '../utils/agent-manager-api';

const AGENT_MANAGER_FILE_PATH = '/rest/agent-manager-file/';

/**
 * The `src` a Files → Media card can actually display (PSTUDIO-4354).
 *
 * An Agent Manager file's `url` is a relative path to an authenticated route on the admin
 * host, so an <img> pointed at it shows nothing. Its bytes are fetched with credentials
 * and shown through an object URL, released when the card goes away or the url changes.
 * `undefined` until then, and on failure — the card's fallback image covers both. A file
 * whose type says it is not an image (a video, a PDF) is not downloaded at all: it could
 * not be previewed, and reading it whole only costs memory. Any other url (a legacy asset)
 * is used directly.
 */
export function useAssetPreviewSrc(
  url: string,
  mimeType?: string | null
): string | undefined {
  const isAgentManagerFile = url.startsWith(AGENT_MANAGER_FILE_PATH);
  const isPreviewable = !mimeType || mimeType.startsWith('image/');
  // Keyed by the url it was made for: after a url change one render still runs before the
  // effect's cleanup, and it must not show the previous file's (already revoked) blob.
  const [preview, setPreview] = useState<{ url: string; src: string }>();

  useEffect(() => {
    if (!isAgentManagerFile || !isPreviewable) return undefined;

    let current = true;
    let created: string | undefined;

    fetchAgentManagerFileBlob(url, mimeType)
      .then((blob) => {
        if (!current) return;
        created = URL.createObjectURL(blob);
        setPreview({ url, src: created });
      })
      .catch(() => {
        // The fallback image stays; a preview is not worth an error toast per card.
      });

    return () => {
      current = false;
      if (created) URL.revokeObjectURL(created);
    };
  }, [isAgentManagerFile, isPreviewable, url, mimeType]);

  if (!isAgentManagerFile) return url;
  return preview?.url === url ? preview.src : undefined;
}
